import Vue from 'vue';
import Component from 'vue-class-component';
import EdFormModel from '@/models/ed-form.model';
import EdInputModel from '@/models/ed-input.model';
import { maxLength, required } from 'vuelidate/lib/validators';
import { Prop } from 'vue-property-decorator';
import { TOAST_TYPES } from '@/plugins/ed-toast/values/ed-toast.values';
import { IRequestSaveHoliday } from '@/tools/interfaces/request-save-holiday.interface';
import { DATE_TYPES_OPTIONS, HOLIDAY_CALENDAR_TYPE, SCHOOL_DATE_TYPE, SCHOOL_DAYS_CALENDAR_COLORS, SCHOOL_DAYS_EVENTS, SCHOOL_DAY_STATUS, _SCHOOL_CALENDAR_STORE } from '@/tools/values/school-calendar.values';
import KEYS from '@/values/keys';
import { SchoolDay } from '@/tools/models/school-day.model';
import Moment from 'moment';
import { ICreateSchoolDayModalParams } from '@/tools/interfaces/create-school-day-modal-params.interface';
import { IRequestDeleteSchoolDay } from '@/tools/interfaces/request-delete-schoolday.interface';
import { IRequestDeleteHoliday } from '@/tools/interfaces/request-delete-holiday.interface';

@Component({
  name: 'ed-create-school-day'
})
export default class EdCreateSchoolDayComponent extends Vue {

  @Prop() readonly params: ICreateSchoolDayModalParams;

  public dateOptions: any[] = [];
  public dateOptionSeclected: any = null;
  public form: any = null;
  public saving: boolean = false;
  public removing: boolean = false;
  public schoolDateType: string = SCHOOL_DATE_TYPE.SCHOOL_CALENDAR;
  public schoolDateTypes = SCHOOL_DATE_TYPE;
  public isUSHoliday: boolean = false;
  public isLoad = false;

  get schoolDayColor(): string {
    if(this.isSchoolCalendarSelected) {
      if(this.params.edition) {
        let calendars: SchoolDay[] = this.$store.state.SchoolCalendarStore.schooldays;
        let schoolDay = calendars.find(sd => sd.schoolDayId == this.params.id);
        return schoolDay.color;
      }
    }
    return '';
  }

  get showNonSchoolDays() {
    return this.$store.state.SchoolCalendarStore.showNonSchoolDays;
  }

  get organizationId() {
    let institutions: any[] = this.$store.state.ToolsStore.institutionInfo;
    return institutions.length > 0 ? institutions[0].organizationId : null;
  }

  get isSchoolCalendarSelected() {
    return this.schoolDateType == SCHOOL_DATE_TYPE.SCHOOL_CALENDAR;
  }

  get descriptionLabel() {
    if (this.isSchoolCalendarSelected) {
      return this.$i18n.t('tools.schooldays.periodName');
    } else {
      return this.$i18n.t('common.description');
    }
  }

  get startDateLabel() {
    if (this.isSchoolCalendarSelected) {
      return this.$i18n.t('tools.schooldays.startAt');
    } else {
      return this.$i18n.t('common.from');
    }
  }

  get endDateLabel() {
    if (this.isSchoolCalendarSelected) {
      return this.$i18n.t('tools.schooldays.endAt');
    } else {
      return this.$i18n.t('common.to');
    }
  }

  get buttonsDisabled() {
    return this.saving || this.removing;
  }

  get workingDays() {
    return this.$store.state.SchoolCalendarStore.workingDays.map(wd => {
      return wd.checked;
    });
  }

  get schooldaysActive() {
    return this.$store.state.SchoolCalendarStore.schooldaysActive;
  }

  get holidays() {
    return this.$store.state.SchoolCalendarStore.holidays;
  }

  get hasDisclamer() {
    let res = false;
    if(this.dateOptionSeclected === SCHOOL_DAY_STATUS.OBSERVED) {
      if(this.schooldaysActive) {
        const date = Moment(this.form.date.value).format('YYYY-MM-DD');
        const isAfterStartDate = Moment(date).isAfter(this.schooldaysActive.startDate);
        const isBeforeEndDate = Moment(date).isBefore(this.schooldaysActive.endDate);
        res = isAfterStartDate && isBeforeEndDate;
      }
    }
    return res;
  }

  get startDateDisabled() {
    if(this.params.edition && this.isSchoolCalendarSelected) {
      if(Moment(this.params.date).isBefore(Moment())) {
        return true;
      }
    }
    return this.isUSHoliday;
  }

  get currentDate () {
    return Moment(this.params.date).format('YYYY-MM-DD')
  }

  get currentToDate () {
    if (!this.form.date.value) {
      return Moment(this.params.endAt).format('YYYY-MM-DD')
    }

    let date = Moment(this.form.date.value).format('YYYY-MM-DD')
    if(this.isSchoolCalendarSelected) {
      return this.getNextAvailableDate(date);
    }

    return date;
  }

  get minDate () {
    return Moment().format('YYYY-MM-DD')
  }

  get minToDate() {
    let minValue = Moment(this.form.date.value).format('YYYY-MM-DD');
    if(this.isSchoolCalendarSelected) {
      minValue = Moment(this.form.date.value).add(1, 'day').format('YYYY-MM-DD');
    }

    return Moment(minValue).format('YYYY-MM-DD');
  }

  get startDateErrors() {
    if (!this.form.date.value) {
      return this.$i18n.t('errors.required');
    }
    
    return '';
  }

  get endAtDateErrors() {

    if (!this.form.endAt.value && !this.form.date.value) {
      return this.$i18n.t('errors.required');
    }

    if (!this.form.endAt.value) {
      let minValue = Moment(this.form.date.value).format('YYYY-MM-DD');
      if (this.isSchoolCalendarSelected) {
        minValue = Moment(this.form.date.value).add(1, 'day').format('YYYY-MM-DD');
      }
      
      return this.$i18n.t('errors.minDateValue', {min: minValue})
    }

    if (!this.form.endAt.value) {
      return this.$i18n.t('errors.required');
    }
    
    return '';
  }

  getNextAvailableDate(date): string {
    let nextDate = Moment(date).add(1, 'day').format('YYYY-MM-DD');
    if (this.workingDays.at(Moment(nextDate).day())) {
      return nextDate;
    } else {
      return this.getNextAvailableDate(nextDate);
    }
  }

  onSchoolDayTypeChanged() {
    if(this.isLoad) {
      let minValue = Moment(this.form.date.value).format('YYYY-MM-DD');
      if(this.isSchoolCalendarSelected) {
        minValue = Moment(this.form.date.value).add(1, 'day').format('YYYY-MM-DD');
      }
      this.form.setValidator('endAt', 'minValue', minValue);
      let endValue = this.form.endAt.value;
      if(Moment(minValue) > Moment(endValue)) {
        this.form.set('endAt', '');
        this.form.setValid('endAt', false);
      }
    }
  }

  onHolidayTypeChanged(value) {
    this.dateOptionSeclected = value.key;
  }

  init(
    description: string = '',
    startDate: string = '',
    endDate: string = '') {

    this.form = EdFormModel([
      EdInputModel('description', 
        description, {
        required,
        maxLength: maxLength(60)
      }),
      EdInputModel('date', startDate, { required }),
      EdInputModel('endAt', endDate, { required })
    ]);
    
    if(this.params.edition || !this.isSchoolCalendarSelected) {
      this.form.setValid('date', true);
      this.form.setValid('endAt', true);
    }

  }

  onStartDateChanged(value) {
    if(!value) {
      this.form.set('endAt', '');
      this.form.setValid('endAt', false);
    }

    let endValue = this.form.endAt.value;
    if(Moment(value) > Moment(endValue) || (this.isSchoolCalendarSelected && Moment(value) >= Moment(endValue))) {
      this.form.set('endAt', '');
      this.form.setValid('endAt', false);
    }

    this.onSchoolDayTypeChanged();
  }

  closeModal() {
    this.$EdModal.closeModal();
  }

  saveDate() {
    if(this.isSchoolCalendarSelected) {
      this.saveSchoolDays();
    } else {
      this.saveHoliday();
    }
  }

  removeDate() {
    this.$EdModal.createConfirmationModal({
      body: this.$i18n.t('tools.schooldays.confirmRemoveDate'),
      actionText: this.$i18n.t('common.remove'),
      action: (res) => {
        if(this.isSchoolCalendarSelected) {
          this.removeSchoolDays();
        } else {
          this.removeHoliday();
        }
      }
    });
  }

  /* Holidays */
  async saveHoliday() {
    this.saving = true;

    try {
      const data: IRequestSaveHoliday = {
        ownerId: this.organizationId,
        ownerType: HOLIDAY_CALENDAR_TYPE.ORGANIZATION,
        holidayPlanId: this.params.id,
        name: this.form.description.value,
        startDate: this.form.date.value.substr(0, 10),
        endDate: this.form.endAt.value.substr(0, 10),
        status: this.dateOptionSeclected
      };

      const isDayOffBetween = this.getExistHolidaysForThisSelection(
        data.startDate, data.endDate, SCHOOL_DAY_STATUS.OBSERVED, data.holidayPlanId
      );

      if (isDayOffBetween) {
        this.showToastError(`${this.$t('tools.schooldays.errors.errorSavingDayOff')}`);
        
        this.saving = false;
        return;
      }

      const isEventBetween = this.getExistHolidaysForThisSelection(
        data.startDate, data.endDate, SCHOOL_DAY_STATUS.NOTIFIED, data.holidayPlanId
      );

      if (isEventBetween) {
        this.showToastError(`${this.$t('tools.schooldays.errors.errorSavingEvent')}`);
        
        this.saving = false;
        return;
      }

      await this.$store.dispatch(
        _SCHOOL_CALENDAR_STORE.ACTIONS.SAVE_HOLIDAYS,
        data
      );
      this.refreshAndClose();
    } catch (err) {
      this.saving = false;

      this.showErrorMessage(err.errors);
    }
  }

  getExistHolidaysForThisSelection(startDate: string, endDate: string, status: string, holidayPlanId: number): boolean {
    let exist = false;

    if (this.dateOptionSeclected === status) {
      const holidaysFiltered = this.holidays.filter(
        el => el.status == status && el.official == false && el.holidayPlanId != holidayPlanId
      );
  
      if (holidaysFiltered.length > 0) {
        const startDateMoment = Moment(startDate);
        const endDateMoment = Moment(endDate);
        const initStartDay = Moment(this.params.date);
        const initEndDay = Moment(this.params.endAt);

        holidaysFiltered.forEach(holiday => {
          const holidayStartDateMoment = Moment(holiday.startDate);
          const holidayEndDateMoment = Moment(holiday.endDate);
          
          if(!initStartDay.isSame(holidayStartDateMoment) && !initEndDay.isSame(holidayEndDateMoment)) {
            const isStartdayBetween = startDateMoment.isBetween(holidayStartDateMoment, holidayEndDateMoment, null, '[]');
            const isEnddayBetween = endDateMoment.isBetween(holidayStartDateMoment, holidayEndDateMoment, null, '[]');

            const isStartdayHolidayBetween = holidayStartDateMoment.isBetween(startDateMoment, endDateMoment, null, '[]');
            const isEnddayHolidayBetween = holidayEndDateMoment.isBetween(startDateMoment, endDateMoment, null, '[]');
    
            if(isStartdayBetween || isEnddayBetween || isStartdayHolidayBetween || isEnddayHolidayBetween) {
              exist = true;
            }
          } else if(startDateMoment.isSame(endDateMoment) ) {
            exist = true;
          }
        });
      }
    }

    return exist;
  }

  showToastError(msg: string) {
    this.$EdToast.create({ 
      msg,
      type: TOAST_TYPES.DANGER,
      top: '3rem',
      timeout: 3000
    });
  }

  async removeHoliday() {
    try {
      this.removing = true;
      let data: IRequestDeleteHoliday = {
        holidayPlanId: this.params.id
      };
      await this.$store.dispatch(
        _SCHOOL_CALENDAR_STORE.ACTIONS.DELETE_HOLIDAYS,
        data
      );
      this.refreshAndClose();
    } catch(err) {
      this.removing = false;
      this.showErrorMessage();
    }
  }
 
  /* School Days */
  async saveSchoolDays() {
    try {
      this.saving = true;
      let data: SchoolDay = {
        schoolDayId: this.params.id,
        organizationId: this.organizationId,
        description: this.form.description.value,
        startDate: this.form.date.value.substr(0, 10),
        endDate: this.form.endAt.value.substr(0, 10),
        color: this.schoolDayColor ? this.schoolDayColor : SCHOOL_DAYS_CALENDAR_COLORS.SCHOOL_DAYS[0]
      };
      await this.$store.dispatch(
        _SCHOOL_CALENDAR_STORE.ACTIONS.SAVE_SCHOOLDAYS, 
        data
      );
      this.refreshAndClose();
    } catch(err) {
      this.saving = false;
      this.showErrorMessage(err.data.errors);
    }
  }

  async removeSchoolDays() {
    try {
      this.removing = true;
      let data: IRequestDeleteSchoolDay = {
        schoolDayId: this.params.id
      };
      await this.$store.dispatch(
        _SCHOOL_CALENDAR_STORE.ACTIONS.DELETE_SCHOOLDAYS,
        data
      );
      this.refreshAndClose();
    } catch(err) {
      this.removing = false;
      this.showErrorMessage();
    }
  }

  refreshAndClose() {
    const $root: any = this.$root;
    this.saving = false;
    this.removing = false;
    $root.initSchoolCalendar();
    setTimeout(() => {
      this.$edEventBus.$emit(SCHOOL_DAYS_EVENTS.REFRESH_CALENDAR);
      this.closeModal();
    }, 200);
  }

  showErrorMessage(err?: any[]) {
    let body = this.$i18n.t('tools.schooldays.errors.errorSavingDateMsg');
    if(err) { 
      let code = `errors.codes.${err[0].status}`;
      body = this.$i18n.t(code) !== code ? this.$i18n.t(code) : body;
    }
    this.$EdModal.createMessageModal({
      body,
      title: this.$i18n.t('tools.schooldays.errors.errorSavingDateTitle'),
      type: KEYS.EVENT_TYPES.DANGER
    });
  }

  setHolidaysTypes() {
    this.dateOptions = DATE_TYPES_OPTIONS.map(o => {
      return {
        ...o,
        value: this.$i18n.t(`tools.schooldays.${o.value}`)
      }
    });
    if(this.params.status) {
      this.dateOptionSeclected = this.params.status;
    } else {
      this.dateOptionSeclected = this.dateOptions[0].key;
    }
  }

  created() {
    let isUSHoliday = this.params.type == SCHOOL_DATE_TYPE.US_HOLIDAYS;
    this.isUSHoliday = isUSHoliday;
    this.schoolDateType = isUSHoliday ? SCHOOL_DATE_TYPE.DAY_OFF : this.params.type;
    
    this.init(
      this.params.description,
      this.params.date,
      this.params.endAt
    );
    this.setHolidaysTypes();
    setTimeout(() => { this.isLoad = true; }, 1000);
  }

}